import { healthWorkoutWindow } from "./healthImportMatching"
import { hasDirectWatchRecording } from "./watchPlatform"

const MINUTE = 60000
const ownerOf = execution => String(execution?.user_id?._id || execution?.user_id || "")
const familyOf = execution => execution.healthActivityFamily || (execution.hyroxSegments?.length ? "mixed" : Number(execution.liftedWeight) > 0 ? "strength" : "")

// Automatic matches must be unique in BOTH directions, including weaker rivals.
// Estimated timestamps, incomplete result sets and unknown sport types are suggestions only.
export function matchHealthActivities(workouts, executions, owner, complete = true, now = Date.now()) {
  const own = executions.filter(item => owner && ownerOf(item) === owner && item._id && !item.healthImportRestricted && !hasDirectWatchRecording(item))
  const pairs = []
  for (const workout of workouts) {
    if (workout.provider !== "health_connect" || workout.existingLink) continue
    const from = Date.parse(workout.startedAt), to = Date.parse(workout.endedAt)
    if (!Number.isFinite(from) || to <= from || to > now || to - from > 7 * 86400000) continue
    for (const execution of own) {
      if (execution.healthImport) continue
      const window = healthWorkoutWindow(execution, now)
      const overlap = Math.max(0, Math.min(to, window.to) - Math.max(from, window.from))
      const union = Math.max(to, window.to) - Math.min(from, window.from)
      const ratio = union > 0 ? overlap / union : 0
      const startGap = Math.abs(from - window.from), endGap = Math.abs(to - window.to)
      if (ratio < .35 || Math.min(startGap, endGap) > 30 * MINUTE) continue
      const sameFamily = Boolean(familyOf(execution) && familyOf(execution) === workout.activityFamily && workout.activityFamily !== "other")
      const suppressed = (workout.dismissedExecutionIds || []).includes(String(execution._id))
      pairs.push({ workoutId: workout.workoutId, executionId: String(execution._id), execution, workout,
        score: ratio, suppressed,
        strict: window.exact && sameFamily && ratio >= .85 && startGap <= 5 * MINUTE && endGap <= 5 * MINUTE && to - from >= 5 * MINUTE,
      })
    }
  }
  return pairs.sort((a, b) => b.score - a.score).map(pair => ({ ...pair,
    automatic: complete && pair.strict && Number.isFinite(Date.parse(pair.execution.updatedAt)) && !pair.suppressed &&
      pairs.filter(other => other.workoutId === pair.workoutId).length === 1 &&
      pairs.filter(other => other.executionId === pair.executionId).length === 1,
  }))
}

// Explain excluded, overlapping own trainings without making them linkable.
// Keep the same loose time bounds as manual suggestions; never infer a link.
export function unavailableHealthActivityMatches(workout, executions, owner, now = Date.now()) {
  if (!owner || workout?.provider !== "health_connect" || workout.existingLink) return []
  const from = Date.parse(workout.startedAt), to = Date.parse(workout.endedAt)
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to > now || to - from > 7 * 86400000) return []
  const matches = []
  for (const execution of executions) {
    if (!execution?._id || ownerOf(execution) !== owner) continue
    const reason = execution.healthImportRestricted ? "restricted" : execution.healthImport ? "linked" : hasDirectWatchRecording(execution) ? "watch" : ""
    if (!reason) continue
    const window = healthWorkoutWindow(execution, now)
    if (!Number.isFinite(window.from) || !Number.isFinite(window.to) || window.to <= window.from) continue
    const overlap = Math.max(0, Math.min(to, window.to) - Math.max(from, window.from))
    const union = Math.max(to, window.to) - Math.min(from, window.from)
    const score = union > 0 ? overlap / union : 0
    if (score < .35 || Math.min(Math.abs(from - window.from), Math.abs(to - window.to)) > 30 * MINUTE) continue
    matches.push({ executionId: String(execution._id), title: execution.title || "Training", reason, score,
      ...window, duration: Number(execution.duration) })
  }
  return matches.sort((a, b) => b.score - a.score)
}
